import { ReloadOutlined } from '@ant-design/icons'
import sickLeaveRepository from '@app/api/repositories/sick-leave-repository'
import { ISickLeave } from '@app/interfaces/sick-leave'
import { openNotificationWithIcon } from '@app/shared/notification/notification'
import { setDocumentTitle } from '@app/utils/set-document-title/set-document-title'
import { Button, Card, Table } from 'antd'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const EMPLOYEE_STATUSES = 'WORKING,DECREE,VACATION,MEDICAL'

const EmployeeSickLeaveHistory: React.FC<{ employeePinfl: string }> = ({
  employeePinfl,
}) => {
  const [t] = useTranslation()

  const [reloading, setReloading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [data, setData] = useState<ISickLeave[]>([])

  setDocumentTitle(t('employee.sickLists'))

  const getSickeLeaveData = async () => {
    try {
      setIsFetching(true)
      const response = await sickLeaveRepository.getEmployeeSickLeaves(
        employeePinfl,
      )
      setData(response.data)
    } catch (error) {
      openNotificationWithIcon('error', t('employee.failGetSickLeaveHistory'))
    } finally {
      setIsFetching(false)
    }
  }

  const reloadAndGetData = async () => {
    try {
      setReloading(true)
      await sickLeaveRepository.reload(employeePinfl)
      getSickeLeaveData()
    } catch (error) {
      openNotificationWithIcon('error', t('employee.failGetSickLeaveHistory'))
    } finally {
      setReloading(false)
    }
  }

  useEffect(() => {
    getSickeLeaveData()
  }, [employeePinfl])

  const columns = [
    {
      title: t('employee.serialNumber'),
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      width: `${100 / 5}%`,
    },
    {
      title: t('employee.medicalInstitution'),
      dataIndex: 'medicalInstitution',
      key: 'medicalInstitution',
      width: `${100 / 5}%`,
    },
    {
      title: t('employee.reason'),
      dataIndex: 'reason',
      key: 'reason',
      width: `${100 / 5}%`,
    },
    {
      title: t('employee.diagnosis'),
      dataIndex: 'diagnosis',
      key: 'diagnosis',
      width: `${100 / 5}%`,
    },
    {
      title: t('employee.duration'),
      dataIndex: 'duration',
      key: 'start',
      width: `${100 / 5}%`,
      render: (duration: any) => {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '5px',
            }}>
            {duration?.map?.((item: any) => {
              return (
                <div key={item.key}>
                  {item.start} - {item.end}
                </div>
              )
            })}
          </div>
        )
      },
    },
  ]

  const tableData = data.length
    ? data?.map((item, index) => {
        return {
          key: index,
          index: index + 1,
          serialNumber: item.serialNumber,
          medicalInstitution: item.medicalInstitution,
          reason: item.reason,
          diagnosis: item.diagnosis,
          duration: item.duration,
        }
      })
    : []

  return (
    <>
      <Card
        title={t('employee.sickLists')}
        extra={
          <Button
            type="primary"
            loading={reloading}
            onClick={reloadAndGetData}
            icon={<ReloadOutlined />}>
            {t('employee.reload')}
          </Button>
        }
        style={{ marginTop: 16 }}>
        <Table
          loading={isFetching}
          pagination={false}
          dataSource={tableData}
          columns={columns}
          rowClassName="table-row"
          locale={{
            emptyText: (
              <div
                style={{
                  textAlign: 'center',
                  padding: '30px 0',
                }}>
                {t('general.noData')}
              </div>
            ),
          }}
        />
      </Card>
    </>
  )
}

export default EmployeeSickLeaveHistory
